"use client"

import { SignUp, ClerkLoading, ClerkLoaded } from "@clerk/nextjs"
import { useCallback, useEffect, useRef, useState } from "react"
import { useTheme } from "next-themes"
import { useSearchParams } from "next/navigation"
import { useI18n } from "@/providers/i18n"

export default function Page() {
  const { t } = useI18n()
  const { setTheme } = useTheme()
  const [langLabel, setLangLabel] = useState('EN')
  const [inviteCode, setInviteCode] = useState<string | null>(null)
  const [inviteValid, setInviteValid] = useState<boolean | null>(null)
  const [inviteChecking, setInviteChecking] = useState<boolean>(false)
  const [lastCheckedInviteCode, setLastCheckedInviteCode] = useState('')
  const [isZh, setIsZh] = useState(false)
  // 移动端注册/登录后应进入 /studio（传统生成工作台），桌面端进入 /projects（画布）。
  const [defaultRedirectUrl, setDefaultRedirectUrl] = useState('/projects')
  const inviteValidationSeq = useRef(0)
  const searchParams = useSearchParams()
  const redirectUrl = (() => {
    let raw: string | null = null
    try { raw = searchParams.get('redirect_url') } catch {}
    const v = String(raw || '').trim()
    if (!v) return undefined
    // Prevent open redirects: allow only same-origin URLs or relative paths.
    if (v.startsWith('/') && !v.startsWith('//')) return v
    if (v.startsWith('http://') || v.startsWith('https://')) {
      try {
        const u = new URL(v)
        if (typeof window !== 'undefined' && u.origin === window.location.origin) {
          return `${u.pathname}${u.search}${u.hash}`
        }
      } catch {}
    }
    return undefined
  })()
  useEffect(() => {
    try {
      const cur = (window.localStorage.getItem('locale') || navigator.language || 'en').toLowerCase()
      setLangLabel(cur.startsWith('zh') ? 'EN' : '中文')
      setIsZh(cur.startsWith('zh'))
    } catch {}
    try {
      const ua = navigator.userAgent || ''
      const isMobileOrTablet = /Mobi|Android|iPhone|iPod|iPad|Tablet|Silk|Kindle|PlayBook|BlackBerry|Opera Mini|IEMobile/i.test(ua)
      setDefaultRedirectUrl(isMobileOrTablet ? '/studio' : '/projects')
    } catch {}
  }, [])
  useEffect(() => {
    try {
      const stored = (typeof window !== 'undefined' ? window.localStorage.getItem('theme') : '') || ''
      const val = stored.toLowerCase()
      if (val === 'light' || val === 'dark') {
        setTheme(val)
      }
    } catch {}
  }, [setTheme])
  useEffect(() => {
    try {
      const fromQuery = String(searchParams?.get('invite') || '').trim()
      const normalized = fromQuery ? fromQuery.toUpperCase() : ''
      setInviteCode(normalized || null)
      if (!normalized) {
        inviteValidationSeq.current += 1
        setInviteValid(null)
        setInviteChecking(false)
        setLastCheckedInviteCode('')
      }
    } catch {}
  }, [searchParams])
  const validateInviteCode = useCallback(async (code: string) => {
    const normalized = code.trim().toUpperCase()
    if (!normalized) {
      inviteValidationSeq.current += 1
      setInviteValid(null)
      setInviteChecking(false)
      setLastCheckedInviteCode('')
      return
    }
    const seq = inviteValidationSeq.current + 1
    inviteValidationSeq.current = seq
    try {
      setInviteChecking(true)
      setInviteValid(null)
      const res = await fetch(`/api/invite/validate?code=${encodeURIComponent(normalized)}`)
      const data = await res.json().catch(() => ({}))
      if (inviteValidationSeq.current !== seq) return
      if (!res.ok) setInviteValid(null)
      else setInviteValid(Boolean((data as any)?.valid))
      setLastCheckedInviteCode(normalized)
    } catch {
      if (inviteValidationSeq.current !== seq) return
      setInviteValid(null)
      setLastCheckedInviteCode(normalized)
    } finally {
      if (inviteValidationSeq.current === seq) setInviteChecking(false)
    }
  }, [])
  useEffect(() => {
    const fromQuery = String(searchParams?.get('invite') || '').trim().toUpperCase()
    if (fromQuery) validateInviteCode(fromQuery)
  }, [searchParams, validateInviteCode])
  const toggleLang = () => {
    try {
      const cur = (window.localStorage.getItem('locale') || navigator.language || 'en').toLowerCase()
      const to = cur.startsWith('zh') ? 'en' : 'zh'
      window.localStorage.setItem('locale', to)
      try { document.cookie = `locale=${to}; path=/; max-age=31536000`; } catch {}
      window.location.reload()
    } catch {}
  }
  const normalizedInviteCode = String(inviteCode || '').trim().toUpperCase()
  const inviteRedirectCode = inviteValid === true && lastCheckedInviteCode === normalizedInviteCode ? normalizedInviteCode : ''
  const appendInviteToRedirect = (target: string) => {
    if (!inviteRedirectCode) return target
    try {
      const url = new URL(target, typeof window !== 'undefined' ? window.location.origin : 'https://flowmuse.ai')
      url.searchParams.set('invite', inviteRedirectCode)
      return `${url.pathname}${url.search}${url.hash}`
    } catch {
      const separator = target.includes('?') ? '&' : '?'
      return `${target}${separator}invite=${encodeURIComponent(inviteRedirectCode)}`
    }
  }
  const signUpRedirectUrl = appendInviteToRedirect(redirectUrl ?? defaultRedirectUrl)
  return (
    <div className="min-h-screen bg-background text-foreground relative overflow-hidden">
      {/* Global subtle grid overlay (light) */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.03] dark:hidden"
        style={{
          backgroundImage:
            'linear-gradient(to right, rgba(0,0,0,0.65) 1px, transparent 1px), linear-gradient(to bottom, rgba(0,0,0,0.65) 1px, transparent 1px)',
          backgroundSize: '14px 14px',
        }}
        aria-hidden="true"
      />
      {/* Global subtle grid overlay (dark) */}
      <div
        className="pointer-events-none absolute inset-0 hidden dark:block opacity-[0.05]"
        style={{
          backgroundImage:
            'linear-gradient(to right, rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.5) 1px, transparent 1px)',
          backgroundSize: '14px 14px',
        }}
        aria-hidden="true"
      />
      <header className="flex items-center justify-between p-6">
        <div className="flex space-x-2">
          <div className="h-2 w-2 rounded-full bg-foreground"></div>
          <div className="h-2 w-2 rounded-full bg-foreground"></div>
        </div>
        <div className="flex items-center space-x-6">
          <button className="text-sm" onClick={toggleLang}>{langLabel}</button>
        </div>
      </header>

      <main className="relative px-6 pt-12">
        {/* Gradient blob */}
        <div
          className="absolute right-0 top-0 h-[300px] w-[300px] animate-pulse rounded-full bg-gradient-to-br from-pink-400 via-orange-300 to-yellow-200 opacity-70 blur-3xl"
          aria-hidden="true"
        />
        

        <div className="relative">
          <h1 className="max-w-3xl text-6xl font-light leading-tight tracking-tight">
            FlowMuse
            <br />
            {t('The Best AI Canvas')}
            <br />
            In The World
          </h1>

          {/* <div className="mt-24 flex justify-between">
            <div className="max-w-md">
              <Button variant="outline" className="rounded-full border-2 px-8">
                <span className="relative">
                  DISCUSS THE PROJECT
                  <div className="absolute -left-4 -right-4 -top-4 -bottom-4 animate-spin-slow rounded-full border border-black opacity-50"></div>
                </span>
              </Button>
              <p className="mt-8 text-sm leading-relaxed text-gray-600">
                WE ARE THE LEADERS IN WEB & MOBILE
                <br />
                DESIGN AND DEVELOPMENT INDUSTRY.
              </p>
            </div>

            <div className="flex items-end">
              <div className="flex items-center space-x-2">
                <span className="text-sm">WHO WE ARE</span>
                <span className="h-px w-12 bg-black"></span>
              </div>
            </div>
          </div> */}

          <p className="mt-24 max-w-xl text-sm leading-relaxed text-muted-foreground">
            {/* We create quality content and cool ideas. We create websites, applications, 3D design, motion design and
            animation. We bring the most daring ideas to life */}
          </p>
        </div>
        <div className="pointer-events-none fixed inset-0 flex items-center justify-center">
          <div className="pointer-events-auto w-[402px]">
            <div className="fm-signup-merged overflow-hidden rounded-2xl border bg-background/95 shadow-sm backdrop-blur">
              <div className="border-b border-border/60 bg-muted/30 px-6 pt-5 pb-4">
                <label className="block text-sm font-medium text-foreground">
                  {t('Invite code (optional)')}
                </label>
                <input
                  value={inviteCode ?? ''}
                  onChange={(event) => {
                    const value = event.target.value.replace(/[^0-9a-z]/gi, '').toUpperCase().slice(0, 12)
                    inviteValidationSeq.current += 1
                    setInviteCode(value || null)
                    setInviteValid(null)
                    setInviteChecking(false)
                    setLastCheckedInviteCode('')
                  }}
                  onBlur={() => {
                    const code = String(inviteCode || '').trim().toUpperCase()
                    if (code && code !== lastCheckedInviteCode) validateInviteCode(code)
                  }}
                  placeholder={t('Enter invite code')}
                  className="mt-2 h-10 w-full rounded-lg border border-input bg-background px-3 text-sm uppercase outline-none transition focus:border-ring focus:ring-2 focus:ring-ring/20"
                />
                <p className={`mt-2 text-xs ${
                  inviteValid === true
                    ? 'text-green-600 dark:text-green-500'
                    : inviteValid === false
                      ? 'text-red-600 dark:text-red-500'
                      : 'text-muted-foreground'
                }`}>
                  {inviteChecking
                    ? t('Checking invite code...')
                    : inviteCode
                      ? inviteValid === true
                        ? t('Invite code is valid. You can claim invite rewards after sign-up.')
                        : inviteValid === false
                          ? t('Invalid invite code.')
                          : t('Enter an invite code to claim rewards after sign-up.')
                      : t('Have an invite code? Enter it before signing up.')}
                </p>
              </div>
              <ClerkLoading>
                <div className="flex h-[508px] flex-col items-center justify-center p-6">
                  <p className="mt-4 text-muted-foreground text-sm">{t('Loading...')}</p>
                </div>
              </ClerkLoading>
              <ClerkLoaded>
                <SignUp
                  path="/sign-up"
                  routing="path"
                  forceRedirectUrl={signUpRedirectUrl}
                  fallbackRedirectUrl={signUpRedirectUrl}
                  signInForceRedirectUrl={signUpRedirectUrl}
                  signInFallbackRedirectUrl={signUpRedirectUrl}
                  appearance={{
                    elements: {
                      rootBox: 'w-full',
                      cardBox: 'w-full max-w-full!',
                    },
                  }}
                />
              </ClerkLoaded>
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
